/**
 * useTodayData — fetches all data needed for the Today screen (#36, #48).
 *
 * Handles:
 *  - Parallel fetching of projects, deals, approvals, executive briefing,
 *    executive decisions, and watchlist/due follow-ups
 *  - Per-endpoint error isolation (one 404 doesn't crash the whole page)
 *  - Aborting in-flight requests on unmount
 *  - Executive Agent endpoints are LLM-backed (10–60s) — they never block
 *    the page. The UI renders with fallback data and fills in agent data
 *    when it arrives. Timeout after 90s to avoid indefinite loading.
 */

import { useEffect, useReducer, useRef } from "react";
import { useAINative } from "../../providers/AINativeProvider.js";
import type {
  AttentionProject,
  ScoredDeal,
  Approval,
  Decision,
  DueFollowUp,
  Priority,
} from "./types.js";
import {
  transformProjects,
  transformDelaysToAttention,
  transformDeals,
  transformApprovals,
  transformBriefingPriorities,
  transformBriefingProjects,
  transformBriefingOpportunities,
  transformDecisions,
  transformDueFollowUps,
} from "./transforms.js";

// ─── State ────────────────────────────────────────────────────────────────────

export type FetchStatus = "idle" | "loading" | "success" | "error";

export interface SectionState<T> {
  status: FetchStatus;
  data: T[];
  error?: string;
  /** True when showing fallback/placeholder data rather than live agent data. */
  isFallback?: boolean;
}

export interface TodayDataState {
  projects: SectionState<AttentionProject>;
  deals: SectionState<ScoredDeal>;
  approvals: SectionState<Approval>;
  /** Decision queue from the Executive Agent (/agents/executive/decisions). */
  decisions: SectionState<Decision>;
  /** Due follow-ups from the watchlist (/watchlist/due). */
  dueFollowUps: SectionState<DueFollowUp>;
  /**
   * Priorities from the Executive Agent (/agents/executive/briefing).
   * Populated asynchronously — may remain null while briefing loads.
   */
  briefingPriorities: SectionState<Priority>;
  /** True while the executive briefing is being fetched (LLM-slow). */
  briefingLoading: boolean;
  /** True once the executive briefing has returned live data. */
  briefingLive: boolean;
  userName: string | null;
}

type Action =
  | { type: "LOADING" }
  | { type: "SET_USER"; name: string | null }
  | { type: "SET_PROJECTS"; data: AttentionProject[] }
  | { type: "SET_PROJECTS_ERROR"; error: string }
  | { type: "SET_PROJECT_DELAYS"; data: AttentionProject[] }
  | { type: "SET_DEALS"; data: ScoredDeal[] }
  | { type: "SET_DEALS_ERROR"; error: string }
  | { type: "SET_APPROVALS"; data: Approval[] }
  | { type: "SET_APPROVALS_ERROR"; error: string }
  | { type: "SET_BRIEFING_LOADING" }
  | { type: "SET_BRIEFING_PRIORITIES"; data: Priority[] }
  | { type: "SET_BRIEFING_ERROR" }
  | { type: "SET_DECISIONS"; data: Decision[] }
  | { type: "SET_DECISIONS_ERROR"; error: string }
  | { type: "SET_DUE_FOLLOW_UPS"; data: DueFollowUp[] }
  | { type: "SET_DUE_FOLLOW_UPS_ERROR"; error: string };

const initialSection = <T>(): SectionState<T> => ({
  status: "idle",
  data: [],
  isFallback: false,
});

const initialState: TodayDataState = {
  projects: initialSection<AttentionProject>(),
  deals: initialSection<ScoredDeal>(),
  approvals: initialSection<Approval>(),
  decisions: initialSection<Decision>(),
  dueFollowUps: initialSection<DueFollowUp>(),
  briefingPriorities: initialSection<Priority>(),
  briefingLoading: true,
  briefingLive: false,
  userName: null,
};

function reducer(state: TodayDataState, action: Action): TodayDataState {
  switch (action.type) {
    case "LOADING":
      return {
        ...state,
        projects: { ...state.projects, status: "loading" },
        deals: { ...state.deals, status: "loading" },
        approvals: { ...state.approvals, status: "loading" },
        decisions: { ...state.decisions, status: "loading" },
        dueFollowUps: { ...state.dueFollowUps, status: "loading" },
        briefingPriorities: { ...state.briefingPriorities, status: "loading" },
        briefingLoading: true,
        briefingLive: false,
      };
    case "SET_USER":
      return { ...state, userName: action.name };

    case "SET_PROJECTS":
      return {
        ...state,
        projects: { status: "success", data: action.data, isFallback: false },
      };
    case "SET_PROJECTS_ERROR":
      return {
        ...state,
        projects: { status: "error", data: [], error: action.error, isFallback: true },
      };

    // The delays agent is the authoritative "needs attention" signal — the plain
    // /projects response returns health: null so it never surfaces delays. When
    // delays come back non-empty, they override the projects-derived list so the
    // "Projects Needing Attention" card matches ProjectsPage (#50). Empty delays
    // are ignored (keep whatever /projects produced) rather than clobbering it.
    case "SET_PROJECT_DELAYS":
      if (action.data.length === 0) return state;
      return {
        ...state,
        projects: { status: "success", data: action.data, isFallback: false },
      };

    case "SET_DEALS":
      return {
        ...state,
        deals: { status: "success", data: action.data, isFallback: false },
      };
    case "SET_DEALS_ERROR":
      return {
        ...state,
        deals: { status: "error", data: [], error: action.error, isFallback: true },
      };

    case "SET_APPROVALS":
      return {
        ...state,
        approvals: { status: "success", data: action.data, isFallback: false },
      };
    case "SET_APPROVALS_ERROR":
      return {
        ...state,
        approvals: { status: "error", data: [], error: action.error, isFallback: true },
      };

    case "SET_BRIEFING_LOADING":
      return { ...state, briefingLoading: true };
    case "SET_BRIEFING_PRIORITIES":
      return {
        ...state,
        briefingPriorities: {
          status: "success",
          data: action.data,
          isFallback: false,
        },
        briefingLoading: false,
        briefingLive: true,
      };
    case "SET_BRIEFING_ERROR":
      return {
        ...state,
        briefingPriorities: {
          status: "error",
          data: [],
          isFallback: true,
        },
        briefingLoading: false,
        briefingLive: false,
      };

    case "SET_DECISIONS":
      return {
        ...state,
        decisions: { status: "success", data: action.data, isFallback: false },
      };
    case "SET_DECISIONS_ERROR":
      return {
        ...state,
        decisions: { status: "error", data: [], error: action.error, isFallback: true },
      };

    case "SET_DUE_FOLLOW_UPS":
      return {
        ...state,
        dueFollowUps: { status: "success", data: action.data, isFallback: false },
      };
    case "SET_DUE_FOLLOW_UPS_ERROR":
      return {
        ...state,
        dueFollowUps: {
          status: "error",
          data: [],
          error: action.error,
          isFallback: true,
        },
      };

    default:
      return state;
  }
}

// ─── Constants ────────────────────────────────────────────────────────────────

/** Timeout for LLM-backed agent endpoints (90 seconds). */
const AGENT_TIMEOUT_MS = 90_000;

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTodayData(): TodayDataState {
  const { api, auth } = useAINative();
  const [state, dispatch] = useReducer(reducer, initialState);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    dispatch({ type: "LOADING" });

    // Set user name from auth context immediately if available
    if (auth.user?.name) {
      dispatch({ type: "SET_USER", name: auth.user.name });
    }

    const safeDispatch = (action: Action) => {
      if (mountedRef.current) dispatch(action);
    };

    // ── Fast endpoints (fire and forget per section) ────────────────────────

    // Fetch projects — map to attention list
    api
      .call("/projects")
      .then((data) => {
        const projects = transformProjects(data);
        safeDispatch({ type: "SET_PROJECTS", data: projects });
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "Unavailable";
        safeDispatch({ type: "SET_PROJECTS_ERROR", error: msg });
      });

    // Fetch delays — the authoritative "needs attention" signal. /projects
    // returns health: null, so without this the attention card always reads
    // "all on track" even when milestones are overdue (same bug as ProjectsPage).
    // Degrade silently: if unavailable we keep the /projects-derived list.
    api
      .call("/agents/development/delays")
      .then((data) => {
        safeDispatch({ type: "SET_PROJECT_DELAYS", data: transformDelaysToAttention(data) });
      })
      .catch(() => {
        /* keep projects-derived attention list */
      });

    // Fetch scored deals — A2: backend returns `properties`, not `deals`
    api
      .call("/deals/scored")
      .then((data) => {
        const deals = transformDeals(data);
        safeDispatch({ type: "SET_DEALS", data: deals });
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "Unavailable";
        safeDispatch({ type: "SET_DEALS_ERROR", error: msg });
      });

    // Fetch approval/decision queue (legacy /approvals endpoint)
    api
      .call("/approvals")
      .then((data) => {
        const approvals = transformApprovals(data);
        safeDispatch({ type: "SET_APPROVALS", data: approvals });
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "Unavailable";
        safeDispatch({ type: "SET_APPROVALS_ERROR", error: msg });
      });

    // A4: Fetch due follow-ups from watchlist — degrade gracefully if unavailable
    api
      .call("/watchlist/due")
      .then((data) => {
        const items = transformDueFollowUps(data);
        safeDispatch({ type: "SET_DUE_FOLLOW_UPS", data: items });
      })
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : "Unavailable";
        safeDispatch({ type: "SET_DUE_FOLLOW_UPS_ERROR", error: msg });
      });

    // ── LLM-backed agent endpoints (slow — never block the page) ───────────

    /**
     * A1: Executive briefing — LLM-backed, may take 10–60s.
     * Renders fallback until data arrives; keeps fallback on error/timeout.
     */
    const briefingTimer = setTimeout(() => {
      // Timeout reached — keep fallback, mark as not loading
      safeDispatch({ type: "SET_BRIEFING_ERROR" });
    }, AGENT_TIMEOUT_MS);

    api
      .call("/agents/executive/briefing")
      .then((data) => {
        clearTimeout(briefingTimer);
        const priorities = transformBriefingPriorities(data);
        // Even if priorities is empty, mark as loaded (briefingLive only if we got data)
        if (priorities.length > 0) {
          safeDispatch({ type: "SET_BRIEFING_PRIORITIES", data: priorities });
        } else {
          // Briefing returned but no priorities (empty or unexpected shape)
          safeDispatch({ type: "SET_BRIEFING_ERROR" });
        }

        // Also pull projects + opportunities from the briefing if available
        const briefingProjects = transformBriefingProjects(data);
        if (briefingProjects.length > 0) {
          // Only update projects if the regular endpoint failed
          if (mountedRef.current) {
            // We don't override a successful projects fetch — only supplement
          }
        }
        // newOpportunities from briefing — can supplement deals if needed
        const briefingOpps = transformBriefingOpportunities(data);
        // Suppress unused-variable warning: briefing opps available but we defer to /deals/scored
        void briefingOpps;
      })
      .catch(() => {
        clearTimeout(briefingTimer);
        safeDispatch({ type: "SET_BRIEFING_ERROR" });
      });

    /**
     * A3: Executive decisions — LLM-backed, may take 10–60s.
     * Wires the "Decision Queue" section to real agent data.
     */
    const decisionsTimer = setTimeout(() => {
      safeDispatch({ type: "SET_DECISIONS_ERROR", error: "Agent timeout" });
    }, AGENT_TIMEOUT_MS);

    api
      .call("/agents/executive/decisions")
      .then((data) => {
        clearTimeout(decisionsTimer);
        const decisions = transformDecisions(data);
        safeDispatch({ type: "SET_DECISIONS", data: decisions });
      })
      .catch((err: unknown) => {
        clearTimeout(decisionsTimer);
        const msg = err instanceof Error ? err.message : "Unavailable";
        safeDispatch({ type: "SET_DECISIONS_ERROR", error: msg });
      });

    return () => {
      mountedRef.current = false;
      clearTimeout(briefingTimer);
      clearTimeout(decisionsTimer);
    };
  }, [api, auth.user]);

  return state;
}
